import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Slider } from '@/components/ui/slider';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { ChevronDown, Search, X } from 'lucide-react';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import { useTypesenseSequenceFilter } from '@/hooks/useTypesenseSequenceFilter';
import { useRouteFilters, useCategoryLandingPage } from '@/hooks/useRouteFilters';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { CategoryHero } from '@/components/sequences/CategoryHero';
import { CategoryStats } from '@/components/sequences/CategoryStats';
import { CategorySEO } from '@/components/seo/CategorySEO';
import SequenceBreadcrumbs from '@/components/sequences/SequenceBreadcrumbs';
import { SequenceSearchHeader } from '@/components/sequences/SequenceSearchHeader';
import { SequenceFilterSidebar } from '@/components/sequences/SequenceFilterSidebar';
import { SequenceResultsGrid } from '@/components/sequences/SequenceResultsGrid';
import { SequenceCard } from '@/components/sequences/SequenceCard';
import { getCategoryInfo, fromSlug } from '@/utils/filterUtils';
import { PAGINATION, SEARCH } from '@/constants/app.constants';
import SequencesErrorBoundary from '@/components/error/SequencesErrorBoundary';

interface SearchFilters {
  query: string;
  software: string[];
  holiday: string[];
  genres: string[];
  genreSlugs?: string[];
  artists: string[];
  minPrice?: number;
  maxPrice?: number;
  featured?: boolean;
  isFree?: boolean;
  fileFormats?: string[];
  albums?: string[];
  sort: string;
}


const Sequences = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  const [artistSearchQuery, setArtistSearchQuery] = useState('');
  const [genreSearchQuery, setGenreSearchQuery] = useState('');
  const [albumSearchQuery, setAlbumSearchQuery] = useState('');
  const [facetsExpanded, setFacetsExpanded] = useState({
    software: true,
    holiday: true,
    genres: true,
    artists: true, // Changed to true to keep expanded by default
    albums: true, // Show albums filter by default
    price: true,
    other: false,
  });

  // Get filters from both route parameters and query parameters
  const { filters: routeAndQueryFilters } = useRouteFilters();
  const { shouldShowLanding, categoryType, categoryValue } = useCategoryLandingPage();

  // Initialize filters from URL parameters (now includes route params)
  const [filters, setFilters] = useState<SearchFilters>(() => ({
    query: routeAndQueryFilters.query || '',
    software: routeAndQueryFilters.software || [],
    holiday: routeAndQueryFilters.holiday || [],
    genres: routeAndQueryFilters.genres || [],
    artists: routeAndQueryFilters.artists || [],
    minPrice: routeAndQueryFilters.minPrice,
    maxPrice: routeAndQueryFilters.maxPrice,
    featured: routeAndQueryFilters.featured,
    isFree: routeAndQueryFilters.isFree,
    fileFormats: (routeAndQueryFilters as any).fileFormats || [],
    albums: (routeAndQueryFilters as any).albums || [],
    sort: routeAndQueryFilters.sort || SEARCH.DEFAULT_SORT,
  }));

  // Update filters when route parameters change
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      query: routeAndQueryFilters.query || '',
      software: routeAndQueryFilters.software || [],
      holiday: routeAndQueryFilters.holiday || [],
      genres: routeAndQueryFilters.genres || [],
      artists: routeAndQueryFilters.artists || [],
      minPrice: routeAndQueryFilters.minPrice,
      maxPrice: routeAndQueryFilters.maxPrice,
      featured: routeAndQueryFilters.featured,
      isFree: routeAndQueryFilters.isFree,
      fileFormats: (routeAndQueryFilters as any).fileFormats || [],
      albums: (routeAndQueryFilters as any).albums || [],
      sort: routeAndQueryFilters.sort || SEARCH.DEFAULT_SORT,
    }));
  }, [routeAndQueryFilters]);

  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = PAGINATION.DEFAULT_PAGE_SIZE;

  // Use Typesense search hook
  const { 
    sequences, 
    facets, 
    totalResults, 
    searchTime, 
    isLoading, 
    error, 
    health 
  } = useTypesenseSequenceFilter({
    query: filters.query,
    filters: {
      software: filters.software,
      holidays: filters.holiday,
      // Prefer slug filter; keep name fallback for compatibility
      genreSlugs: (routeAndQueryFilters as any).genreSlugs || [],
      genres: filters.genres,
      artists: filters.artists,
      minPrice: filters.minPrice,
      maxPrice: filters.maxPrice,
      featured: filters.featured,
      isFree: filters.isFree,
      fileFormats: filters.fileFormats,
      albums: filters.albums,
    },
    sort: filters.sort,
    page: currentPage,
    per_page: ITEMS_PER_PAGE,
  });

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (filters.query) params.set('q', filters.query);
    if (filters.software.length > 0) params.set('software', filters.software.join(','));
    if (filters.holiday.length > 0) params.set('holiday', filters.holiday.join(','));
    if (filters.genres.length > 0) params.set('genres', filters.genres.join(','));
    if (filters.artists.length > 0) params.set('artists', filters.artists.join(','));
    if (filters.minPrice !== undefined) params.set('minPrice', filters.minPrice.toString());
    if (filters.maxPrice !== undefined) params.set('maxPrice', filters.maxPrice.toString());
    if (filters.featured !== undefined) params.set('featured', filters.featured.toString());
    if (filters.isFree !== undefined) params.set('free', filters.isFree.toString());
    if (filters.fileFormats && filters.fileFormats.length > 0) params.set('fileFormats', filters.fileFormats.join(','));
    if (filters.albums && filters.albums.length > 0) params.set('albums', filters.albums.join(','));
    if (filters.sort !== SEARCH.DEFAULT_SORT) params.set('sort', filters.sort);
    if (currentPage > 1) params.set('page', currentPage.toString());

    const newSearch = params.toString();
    if (newSearch !== searchParams.toString()) {
      navigate({ search: newSearch }, { replace: true });
    }
  }, [filters, currentPage, navigate, searchParams]);

  // Handle search input
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
  };

  const handleQueryChange = (query: string) => {
    setFilters(prev => ({ ...prev, query }));
    setCurrentPage(1);
  };

  // Filter handlers
  const toggleArrayFilter = (filterType: keyof Pick<SearchFilters, 'software' | 'holiday' | 'genres' | 'artists'>, value: string) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType].includes(value)
        ? prev[filterType].filter(item => item !== value)
        : [...prev[filterType], value]
    }));
    setCurrentPage(1);
  };

  const toggleExtraArrayFilter = (filterType: keyof Pick<SearchFilters, 'fileFormats' | 'albums'>, value: string) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: (prev[filterType] || []).includes(value)
        ? (prev[filterType] || []).filter((v: string) => v !== value)
        : ([...(prev[filterType] || []), value] as any)
    }));
    setCurrentPage(1);
  };

  const toggleBooleanFilter = (filterType: 'featured' | 'isFree') => {
    setFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType] === true ? undefined : true
    }));
    setCurrentPage(1);
  };

  const setPriceRange = (min?: number, max?: number) => {
    setFilters(prev => ({ ...prev, minPrice: min, maxPrice: max }));
    setCurrentPage(1);
  };

  const handleSortChange = (sort: string) => {
    setFilters(prev => ({ ...prev, sort }));
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    // Navigate to base sequences path to clear route-based filters
    navigate('/sequences', { replace: true });
    
    // Reset filters state (this will be handled by useRouteFilters effect)
    setFilters({
      query: '',
      software: [],
      holiday: [],
      genres: [],
      artists: [],
      minPrice: undefined,
      maxPrice: undefined,
      featured: undefined,
      isFree: undefined,
      fileFormats: [],
      albums: [],
      sort: SEARCH.DEFAULT_SORT,
    });
    setCurrentPage(1);
  };

  const removeFilter = (filterType: string, value?: string) => {
    if (filterType === 'query') {
      setFilters(prev => ({ ...prev, query: '' }));
    } else if (filterType === 'featured' || filterType === 'isFree') {
      setFilters(prev => ({ ...prev, [filterType]: undefined }));
    } else if (filterType === 'price') {
      setFilters(prev => ({ ...prev, minPrice: undefined, maxPrice: undefined }));
    } else if (value && ['software', 'holiday', 'genres', 'artists', 'fileFormats', 'albums'].includes(filterType)) {
      setFilters(prev => ({
        ...prev,
        [filterType]: (prev as any)[filterType].filter((item: string) => item !== value)
      }));
    }
    setCurrentPage(1);
  };

  // Active filters for display
  const activeFilters = useMemo(() => {
    const active = [];
    if (filters.query) active.push({ type: 'query', label: `Search: ${filters.query}` });
    filters.software.forEach(s => active.push({ type: 'software', value: s, label: `Software: ${s}` }));
    filters.holiday.forEach(h => active.push({ type: 'holiday', value: h, label: `Holiday: ${h}` }));
    filters.genres.forEach(g => active.push({ type: 'genres', value: g, label: `Genre: ${g}` }));
    filters.artists.forEach(a => active.push({ type: 'artists', value: a, label: `Artist: ${a}` }));
    if (filters.minPrice !== undefined || filters.maxPrice !== undefined) {
      const min = filters.minPrice || 0;
      const max = filters.maxPrice || 1000;
      active.push({ type: 'price', label: `Price: $${min} - $${max}` });
    }
    if (filters.featured) active.push({ type: 'featured', label: 'Featured Only' });
    if (filters.isFree) active.push({ type: 'isFree', label: 'Free Only' });
    return active;
  }, [filters]);

  // Pagination
  const totalPages = Math.ceil(totalResults / ITEMS_PER_PAGE);
  const startResult = (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const endResult = Math.min(currentPage * ITEMS_PER_PAGE, totalResults);

  const FacetSection = ({ title, fieldName, items = [], expanded, onToggle }: {
    title: string;
    fieldName: string;
    items: Array<{ value: string; count: number }>;
    expanded: boolean;
    onToggle: () => void;
  }) => (
    <Collapsible open={expanded} onOpenChange={onToggle}>
      <CollapsibleTrigger asChild>
        <Button variant="ghost" className="w-full justify-between p-2 h-auto">
          <span className="font-medium">{title}</span>
          <ChevronDown className={cn("h-4 w-4 transition-transform", expanded && "rotate-180")} />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-2 p-2">
        <FacetList items={items} fieldName={fieldName} />
      </CollapsibleContent>
    </Collapsible>
  );

  const FacetList = ({ items, fieldName }: { items: Array<{ value: string; count: number }>; fieldName: string }) => {
    const [showAll, setShowAll] = useState(false);
    const DISPLAY_COUNT = 20;
    const visible = showAll ? items : items.slice(0, DISPLAY_COUNT);
    const hasMore = items.length > DISPLAY_COUNT;
    return (
      <div className="space-y-2">
        <ScrollArea className="max-h-48">
          {visible.map(item => (
            <div key={item.value} className="flex items-center space-x-2 py-1">
              <Checkbox
                id={`${fieldName}-${item.value}`}
                checked={(filters as any)[fieldName]?.includes?.(item.value)}
                onCheckedChange={() => toggleArrayFilter(fieldName as any, item.value)}
              />
              <label
                htmlFor={`${fieldName}-${item.value}`}
                className="flex-1 text-sm cursor-pointer flex justify-between"
              >
                <span className="truncate">{item.value}</span>
                <span className="text-muted-foreground ml-2">({item.count})</span>
              </label>
            </div>
          ))}
        </ScrollArea>
        {hasMore && (
          <Button variant="ghost" size="sm" className="w-full" onClick={() => setShowAll(!showAll)}>
            {showAll ? 'Show less' : `Show more (${items.length - DISPLAY_COUNT})`}
          </Button>
        )}
      </div>
    );
  };

  const SearchableFacetSection = ({
    title,
    fieldName,
    items = [],
    expanded,
    onToggle,
    searchQuery,
    onSearchChange,
  }: {
    title: string;
    fieldName: string;
    items: Array<{ value: string; count: number }>;
    expanded: boolean;
    onToggle: () => void;
    searchQuery: string;
    onSearchChange: (q: string) => void;
  }) => {
    const filtered = searchQuery
      ? items.filter((i) => i.value.toLowerCase().includes(searchQuery.toLowerCase()))
      : items;
    const [showAll, setShowAll] = useState(false);
    const DISPLAY_COUNT = 20;
    const visible = showAll ? filtered : filtered.slice(0, DISPLAY_COUNT);
    const hasMore = filtered.length > DISPLAY_COUNT;
    return (
      <Collapsible open={expanded} onOpenChange={onToggle}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="w-full justify-between p-2 h-auto">
            <span className="font-medium">{title}</span>
            <ChevronDown className={cn("h-4 w-4 transition-transform", expanded && "rotate-180")} />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-2 p-2">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder={`Search ${title.toLowerCase()}...`}
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-8 h-9"
            />
          </div>
          <ScrollArea className="max-h-48">
            {visible.map((item) => (
              <div key={item.value} className="flex items-center space-x-2 py-1">
                <Checkbox
                  id={`${fieldName}-${item.value}`}
                  checked={(filters as any)[fieldName]?.includes?.(item.value)}
                  onCheckedChange={() => toggleArrayFilter(fieldName as any, item.value)}
                />
                <label htmlFor={`${fieldName}-${item.value}`} className="flex-1 text-sm cursor-pointer flex justify-between">
                  <span className="truncate">{item.value}</span>
                  <span className="text-muted-foreground ml-2">({item.count})</span>
                </label>
              </div>
            ))}
          </ScrollArea>
          {hasMore && (
            <Button variant="ghost" size="sm" className="w-full" onClick={() => setShowAll(!showAll)}>
              {showAll ? 'Show less' : `Show more (${filtered.length - DISPLAY_COUNT})`}
            </Button>
          )}
        </CollapsibleContent>
      </Collapsible>
    );
  };

  const ArtistFacetSection = ({ items = [], expanded, onToggle }: {
    items: Array<{ value: string; count: number }>;
    expanded: boolean;
    onToggle: () => void;
  }) => {
    // Filter artists based on search query
    const filteredItems = artistSearchQuery
      ? items.filter(item => 
          item.value.toLowerCase().includes(artistSearchQuery.toLowerCase())
        )
      : items;
    const [showAll, setShowAll] = useState(false);
    const DISPLAY_COUNT = 20;
    const visible = showAll ? filteredItems : filteredItems.slice(0, DISPLAY_COUNT);
    const hasMore = filteredItems.length > DISPLAY_COUNT;

    return (
      <Collapsible open={expanded} onOpenChange={onToggle}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="w-full justify-between p-2 h-auto">
            <span className="font-medium">Artists</span>
            <ChevronDown className={cn("h-4 w-4 transition-transform", expanded && "rotate-180")} />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-2 p-2">
          {/* Search box for artists */}
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search artists..."
              value={artistSearchQuery}
              onChange={(e) => setArtistSearchQuery(e.target.value)}
              className="pl-8 h-9"
            />
          </div>
          
          <ScrollArea className="max-h-48">
            {filteredItems.length > 0 ? (
              visible.map(item => (
                <div key={item.value} className="flex items-center space-x-2 py-1">
                  <Checkbox
                    id={`artists-${item.value}`}
                    checked={filters.artists?.includes?.(item.value)}
                    onCheckedChange={() => toggleArrayFilter('artists', item.value)}
                  />
                  <label
                    htmlFor={`artists-${item.value}`}
                    className="flex-1 text-sm cursor-pointer flex justify-between"
                  >
                    <span className="truncate">{item.value}</span>
                    <span className="text-muted-foreground ml-2">({item.count})</span>
                  </label>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground text-center py-2">
                No artists found matching "{artistSearchQuery}"
              </p>
            )}
          </ScrollArea>
          {hasMore && (
            <Button variant="ghost" size="sm" className="w-full mt-2" onClick={() => setShowAll(!showAll)}>
              {showAll ? 'Show less' : `Show more (${filteredItems.length - DISPLAY_COUNT})`}
            </Button>
          )}
        </CollapsibleContent>
      </Collapsible>
    );
  };

  if (error) {
    return (
      <div className="min-h-screen flex flex-col">
        <NavBar />
        <main className="flex-1 container py-8">
          <Card className="max-w-md mx-auto">
            <CardHeader>
              <CardTitle className="text-destructive">Search Error</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">{error}</p>
              <Button onClick={() => window.location.reload()}>Try Again</Button>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  // Get category info for landing page
  const resolvedCategoryValue = categoryType && categoryValue
    ? ((categoryType === 'software' || categoryType === 'holiday')
        ? fromSlug(categoryType, categoryValue)
        : categoryValue)
    : null;
  const categoryInfo = shouldShowLanding && categoryType && resolvedCategoryValue
    ? getCategoryInfo(
        // getCategoryInfo only supports software/holiday; fallback label otherwise
        (categoryType === 'software' || categoryType === 'holiday') ? categoryType : 'software',
        resolvedCategoryValue
      )
    : null;

  // Generate mock stats for category landing pages
  const generateCategoryStats = () => {
    if (!shouldShowLanding || !sequences.length) return null;
    
    const freeSequences = sequences.filter(seq => seq.price === 0).length;
    const ratings = sequences.filter(seq => seq.averageRating > 0);
    const avgRating = ratings.length > 0 
      ? ratings.reduce((sum, seq) => sum + seq.averageRating, 0) / ratings.length 
      : 4.2;
    const totalDownloads = sequences.reduce((sum, seq) => sum + (seq.downloadCount || 0), 0);
    
    return {
      totalSequences: totalResults,
      freeSequences,
      averageRating: avgRating,
      totalDownloads,
      popularTags: sequences
        .flatMap(seq => [seq.software, seq.holiday])
        .filter((tag, index, arr) => arr.indexOf(tag) === index)
        .slice(0, 5),
      trendingSequences: sequences
        .sort((a, b) => (b.downloadCount || 0) - (a.downloadCount || 0))
        .slice(0, 3)
        .map(seq => ({
          id: seq.id,
          title: seq.title || seq.displayName,
          downloads: seq.downloadCount || 0,
          rating: seq.averageRating || 4.0
        }))
    };
  };

  const categoryStats = generateCategoryStats();

  // Helper functions for the decomposed components
  const toggleFacetsExpanded = (facet: string) => {
    setFacetsExpanded(prev => ({
      ...prev,
      [facet]: !prev[facet as keyof typeof prev]
    }));
  };

  const handleFiltersChange = (newFilters: SearchFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleToggleFilters = () => {
    setShowFilters(!showFilters);
  };

  return (
    <SequencesErrorBoundary>
      <>
        {/* SEO - Use CategorySEO for landing pages, regular SEO for search */}
        {shouldShowLanding && categoryType && categoryValue ? (
          <CategorySEO
            type={categoryType}
            value={resolvedCategoryValue || ''}
            totalResults={totalResults}
            pathname={window.location.pathname}
          />
        ) : (
          <Helmet>
            <title>Light Display Sequences - Search & Download</title>
            <meta name="description" content="Search and download holiday light display sequences. Find Christmas, Halloween, and other holiday light shows." />
          </Helmet>
        )}

      <div className="min-h-screen flex flex-col">
        <NavBar />
        
        <main className="flex-1 pt-24">
          {/* Category Landing Page Hero */}
          {shouldShowLanding && categoryType && categoryValue && categoryInfo && (
            <div className="container py-6">
              <CategoryHero 
                type={categoryType}
                value={resolvedCategoryValue || ''}
                totalResults={totalResults}
                className="mb-6"
              />
              {categoryStats && (
                <CategoryStats 
                  type={categoryType}
                  value={resolvedCategoryValue || ''}
                  stats={categoryStats}
                  className="mb-8"
                />
              )}
            </div>
          )}

          {/* Search Header */}
          <SequenceSearchHeader 
            query={filters.query}
            sort={filters.sort}
            totalResults={totalResults}
            searchTime={searchTime}
            isLoading={isLoading}
            shouldShowLanding={shouldShowLanding}
            onQueryChange={handleQueryChange}
            onSortChange={handleSortChange}
            onToggleFilters={handleToggleFilters}
            onSearch={handleSearch}
            showFilters={showFilters}
          />

          {/* Active Filters */}
          {activeFilters.length > 0 && (
            <div className="container py-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">Active filters:</span>
                {activeFilters.map((filter, index) => (
                  <Badge key={index} variant="secondary" className="gap-1">
                    {filter.label}
                    <X 
                      className="h-3 w-3 cursor-pointer" 
                      onClick={() => removeFilter(filter.type, filter.value)}
                    />
                  </Badge>
                ))}
                <Button variant="ghost" size="sm" onClick={clearAllFilters}>
                  Clear all
                </Button>
              </div>
            </div>
          )}

          <div className="container py-6">
            {/* Breadcrumbs */}
            <SequenceBreadcrumbs className="mb-6" />
            
            <div className="flex gap-6">
              {/* Filters Sidebar */}
              <aside className={cn(
                "w-80 shrink-0 sticky top-4 h-[calc(100vh-2rem)] overflow-y-auto",
                !showFilters && "hidden lg:block"
              )}>
                <SequenceFilterSidebar
                  filters={filters}
                  facets={facets}
                  facetsExpanded={facetsExpanded}
                  artistSearchQuery={artistSearchQuery}
                  albumSearchQuery={albumSearchQuery}
                  onFiltersChange={handleFiltersChange}
                  onToggleArrayFilter={toggleArrayFilter}
                  onToggleExtraArrayFilter={toggleExtraArrayFilter}
                  onToggleBooleanFilter={toggleBooleanFilter}
                  onSetPriceRange={setPriceRange}
                  onClearAllFilters={clearAllFilters}
                  onToggleFacetsExpanded={toggleFacetsExpanded}
                  onArtistSearchChange={setArtistSearchQuery}
                  onAlbumSearchChange={setAlbumSearchQuery}
                />
              </aside>

              {/* Results */}
              <div className="flex-1 min-w-0">
                {isLoading ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {Array.from({ length: 12 }).map((_, i) => (
                      <Card key={i} className="overflow-hidden">
                        <Skeleton className="aspect-video w-full" />
                        <CardContent className="p-4">
                          <Skeleton className="h-4 w-full mb-2" />
                          <Skeleton className="h-4 w-2/3 mb-2" />
                          <Skeleton className="h-4 w-1/3" />
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : sequences.length > 0 ? (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
                      {sequences.map((sequence) => (
                        <SequenceCard key={sequence.id} sequence={sequence} />
                      ))}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          variant="outline"
                          disabled={currentPage === 1}
                          onClick={() => setCurrentPage(currentPage - 1)}
                        >
                          Previous
                        </Button>
                        <div className="flex items-center gap-1">
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                            return (
                              <Button
                                key={page}
                                variant={currentPage === page ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setCurrentPage(page)}
                              >
                                {page}
                              </Button>
                            );
                          })}
                        </div>
                        <Button
                          variant="outline"
                          disabled={currentPage === totalPages}
                          onClick={() => setCurrentPage(currentPage + 1)}
                        >
                          Next
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  <Card className="text-center py-12">
                    <CardContent>
                      <h3 className="text-lg font-semibold mb-2">No sequences found</h3>
                      <p className="text-muted-foreground mb-4">
                        Try adjusting your search terms or filters
                      </p>
                      <Button onClick={clearAllFilters}>Clear all filters</Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>

            {/* Mobile Filters Overlay */}
            {showFilters && (
              <div className="lg:hidden fixed inset-0 z-50 bg-background/80 backdrop-blur-xs">
                <div className="fixed inset-y-0 left-0 w-80 bg-background border-r p-4 overflow-y-auto">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold">Filters</h2>
                    <Button variant="ghost" size="sm" onClick={() => setShowFilters(false)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <SequenceFilterSidebar
                    filters={filters}
                    facets={facets}
                    facetsExpanded={facetsExpanded}
                    artistSearchQuery={artistSearchQuery}
                    albumSearchQuery={albumSearchQuery}
                    onFiltersChange={handleFiltersChange}
                    onToggleArrayFilter={toggleArrayFilter}
                    onToggleExtraArrayFilter={toggleExtraArrayFilter}
                    onToggleBooleanFilter={toggleBooleanFilter}
                    onSetPriceRange={setPriceRange}
                    onClearAllFilters={clearAllFilters}
                    onToggleFacetsExpanded={toggleFacetsExpanded}
                    onArtistSearchChange={setArtistSearchQuery}
                    onAlbumSearchChange={setAlbumSearchQuery}
                  />
                </div>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
      </>
    </SequencesErrorBoundary>
  );
};

export default Sequences;